// Live-capture engine: pure functions shared by the cardiac arrest console and
// the critical care console. No React, no platform imports.
//
// KEEP IN SYNC: this file is duplicated byte-for-byte at
//   client/src/pages/patientRecords/ePCR/liveCapture/engine.js
//   mobile/src/components/EPCR/liveCapture/engine.js
// (and catalogue.js alongside it). Edit one, copy to the other.
//
// Model
// -----
// A tap appends an *event* to a log — record.cardiacArrest.events for the
// arrest console, record.criticalCare.events for everything else. When the
// action has a `target`, the tap also creates a real entry in the section the
// rest of the ePCR already uses (treatment.modules / assess.modules /
// vitals.obs) so the intervention shows on the Treatment/Assess screens
// straight away. Event and entry point at each other:
//   event.entryId + event.section  <->  entry.entryId + entry.liveEventId
// Entries created from a tap carry `detailsPending: true` until someone
// completes the action's detail form (on the console, or later from the
// Treatment/Assess screens), which merges the form values into the same
// entry. Console state (CPR on/off, shock counts, phase, clocks) is always
// derived from the event log — nothing else is stored.

import { ACTIONS, FORMS, CONSOLES, CATEGORIES, inductionDose } from './catalogue'

export const LOG_KEYS = {
  arrest: 'cardiacArrest',
  criticalCare: 'criticalCare',
}

// Keys every entry display (Treatment list, EPCRView, PDF, mobile cards)
// should hide - bookkeeping, not clinical content.
export const LIVE_ENTRY_META_KEYS = [
  'entryId',
  'liveEventId',
  'detailsPending',
  'detailsCompletedAt',
  'source',
  'liveAction',
]

export const SECTION_PATHS = {
  treatment: ['treatment', 'modules'],
  assess: ['assess', 'modules'],
  vitals: ['vitals', 'obs'],
}

export const newLiveId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

const nowIso = () => new Date().toISOString()

const getSectionList = (record, section) => {
  const [a, b] = SECTION_PATHS[section] || []
  if (!a) return []
  return record?.[a]?.[b] || []
}

const setSectionList = (record, section, list) => {
  const [a, b] = SECTION_PATHS[section]
  return { ...record, [a]: { ...(record?.[a] || {}), [b]: list } }
}

const entryTimeKey = (section) => (section === 'vitals' ? 'time' : 'dateTime')

export const getAction = (key) => ACTIONS[key] || null
export const getForm = (key) => FORMS[key] || null
export const getCategory = (key) =>
  CATEGORIES.find((category) => category.key === key) || null

// Whether an action is offered on a console of the given level ('Critical
// Care' or anything else = standard). `levels` absent = everyone.
export const isActionAvailable = (action, level) =>
  !!action && (!action.levels || action.levels.includes(level))

// ---------------------------------------------------------------------------
// Building a tap
// ---------------------------------------------------------------------------

const describeChoice = (action, choice) => {
  if (!choice) return undefined
  if (typeof choice === 'string') return choice
  return choice.label || choice.value
}

// Text shown for an event in timelines ("Shock · 150J", "Adrenaline 1mg IV").
export const describeEvent = (event) => {
  if (!event) return ''
  const action = getAction(event.type)
  const base = event.label || action?.label || event.type
  if (!event.detail) return base
  // Counter details already lead with the label ("Shock 3 · 150J")
  if (event.detail.toLowerCase().startsWith(String(base).toLowerCase())) return event.detail
  return `${base} · ${event.detail}`
}

// Build the entry an action creates in its target section, or null.
const buildEntry = ({ action, eventId, entryId, time, choice, drug, counterValue }) => {
  const target = action.target
  if (!target || target.deferEntry) return null
  const section = target.section
  const values = { ...(target.prefill || {}) }
  const choiceValue = typeof choice === 'string' ? choice : choice?.value
  if (target.choiceField && choiceValue) values[target.choiceField] = choiceValue
  if (choice && typeof choice === 'object' && choice.prefill) {
    Object.assign(values, choice.prefill)
  }
  if (drug) {
    values['Medicine Name'] = drug.name
    if (drug.dose) values.Dose = drug.dose
    if (drug.route) values.Route = drug.route
  }
  if (target.counterField && counterValue) values[target.counterField] = counterValue

  const base = {
    entryId,
    liveEventId: eventId,
    liveAction: action.key,
    source: 'live',
    // detailsOptional: the form is offered but nothing is owed (shock energy)
    detailsPending: !!action.detailForm && !action.detailsOptional,
  }
  if (section === 'vitals') return { section, entry: { time, ...base, ...values } }
  return {
    section,
    entry: { moduleName: target.moduleName, dateTime: time, ...base, ...values },
  }
}

// Returns { event, section, entry } for a tap. `choice` is the picked option
// for 'choice' actions, `drug` the {name, dose, route} for drug actions,
// `on` the new state for toggles.
export const buildTap = ({
  actionKey,
  choice,
  drug,
  on,
  time = nowIso(),
  phase,
  category,
  userId,
  log = [],
  extra = {},
  weightKg,
}) => {
  const action = getAction(actionKey)
  if (!action) throw new Error(`Unknown live action ${actionKey}`)
  const eventId = newLiveId()
  const entryId = action.target ? newLiveId() : undefined
  const counterValue = action.counter
    ? countEvents(log, actionKey, (event) => !action.counter.onlyOn || event.data?.on !== false) + 1
    : undefined

  let detail = describeChoice(action, choice)
  if (drug) detail = [drug.name, drug.dose, drug.route].filter(Boolean).join(' ')
  if (action.kind === 'toggle') detail = on ? action.onLabel || 'Started' : action.offLabel || 'Stopped'
  if (counterValue && action.counter?.label) {
    detail = [`${action.counter.label} ${counterValue}`, detail].filter(Boolean).join(' · ')
  }

  const data = { ...extra }
  if (choice !== undefined) data.choice = typeof choice === 'string' ? choice : choice?.value
  if (drug) data.drug = drug
  if (action.kind === 'toggle') data.on = !!on
  if (counterValue) data.count = counterValue

  // A toggle only creates an entry when it is switched on (tourniquet on =
  // a Catastrophic Haemorrhage entry; tourniquet off is just a timestamp).
  const built =
    action.kind === 'toggle' && !on
      ? null
      : buildEntry({ action, eventId, entryId, time, choice, drug, counterValue })

  // Induction: one pending Medicine entry per induction drug so each can be
  // linked to its syringe / batch afterwards.
  const extraEntries = []
  if (action.inductionDrugs?.length) {
    const ratio = typeof choice === 'string' ? choice : choice?.value
    action.inductionDrugs.forEach((name) => {
      const dose = inductionDose(name, ratio, weightKg)
      if (ratio && ratio !== 'Other' && !dose && weightKg) return // 0 mcg/kg - not given
      extraEntries.push({
        section: 'treatment',
        entry: {
          moduleName: 'Medicine',
          dateTime: time,
          entryId: newLiveId(),
          liveEventId: eventId,
          liveAction: 'drug',
          source: 'live',
          detailsPending: true,
          'Medicine Name': name,
          ...(dose ? { Dose: dose } : {}),
          Route: 'IV/IO',
          'Indication': 'PHEA induction',
        },
      })
    })
    if (extraEntries.length) data.entryIds = extraEntries.map((item) => item.entry.entryId)
    if (weightKg) data.weightKg = Number(weightKg)
  }

  // Implied state changes (shock -> compressions back on, ROSC -> CPR off…)
  // - only where the toggle would actually change
  const toggleStates = getToggleStates(log)
  const choiceValue = typeof choice === 'string' ? choice : choice?.value
  const impliedEvents = (action.implies || [])
    .filter((rule) => {
      if (rule.ifChoice && !rule.ifChoice.includes(choiceValue)) return false
      if (rule.ifOn !== undefined && rule.ifOn !== !!on) return false
      const current = !!toggleStates[rule.toggle]?.on
      return current !== rule.on
    })
    .map((rule) => {
      const target = getAction(rule.toggle)
      return {
        id: newLiveId(),
        type: rule.toggle,
        label: target?.label,
        time,
        detail: rule.on ? target?.onLabel || 'Started' : target?.offLabel || 'Stopped',
        ...(phase ? { phase } : {}),
        ...(category ? { category } : {}),
        data: { on: rule.on, implied: true, cause: eventId },
        ...(userId ? { recordedBy: userId } : {}),
      }
    })

  const event = {
    id: eventId,
    type: actionKey,
    label: action.label,
    time,
    ...(detail ? { detail } : {}),
    ...(phase ? { phase } : {}),
    ...(category ? { category } : {}),
    ...(built ? { section: built.section, entryId } : {}),
    ...(Object.keys(data).length ? { data } : {}),
    ...(userId ? { recordedBy: userId } : {}),
  }
  return { event, section: built?.section, entry: built?.entry || null, extraEntries, impliedEvents }
}

// Apply a built tap to a record (immutably). `logKey` is LOG_KEYS.*.
export const applyTap = (record, logKey, tap, logDefaults = {}) => {
  const log = record?.[logKey] || {}
  let next = {
    ...record,
    [logKey]: {
      ...logDefaults,
      ...log,
      events: [...(log.events || []), tap.event, ...(tap.impliedEvents || [])],
    },
  }
  if (tap.entry && tap.section) {
    next = setSectionList(next, tap.section, [...getSectionList(next, tap.section), tap.entry])
  }
  ;(tap.extraEntries || []).forEach(({ section, entry }) => {
    next = setSectionList(next, section, [...getSectionList(next, section), entry])
  })
  return next
}

// A quick chip picked after a tap (shock energy, airway size): appended to
// the event detail and written to the entry's chipField.
export const annotateEvent = (record, logKey, eventId, chip) => {
  const log = record?.[logKey]
  const event = log?.events?.find((item) => item.id === eventId)
  if (!event) return record
  const action = getAction(event.type)
  let next = {
    ...record,
    [logKey]: {
      ...log,
      events: log.events.map((item) =>
        item.id === eventId
          ? {
              ...item,
              detail: [item.detail, chip].filter(Boolean).join(' · '),
              data: { ...(item.data || {}), chip },
            }
          : item,
      ),
    },
  }
  if (action?.chipField && event.section && event.entryId) {
    next = setSectionList(
      next,
      event.section,
      getSectionList(next, event.section).map((item) =>
        item.entryId === event.entryId ? { ...item, [action.chipField]: chip } : item,
      ),
    )
  }
  return next
}

// Undo a tap: the event is kept (voided) for audit; its entry is removed
// unless someone completed its details or linked it to stock, in which case
// it stays and is unlinked. (A chip annotation is not completed details.)
export const voidEvent = (record, logKey, eventId) => {
  const log = record?.[logKey]
  if (!log?.events) return record
  const event = log.events.find((item) => item.id === eventId)
  if (!event) return record
  let next = {
    ...record,
    [logKey]: {
      ...log,
      // Implied events (data.cause) go with the tap that caused them
      events: log.events.map((item) =>
        item.id === eventId || item.data?.cause === eventId
          ? { ...item, voided: true, voidedAt: nowIso() }
          : item,
      ),
    },
  }
  // Induction-style extra entries: drop the ones nobody has touched
  if (event.data?.entryIds?.length) {
    const ids = new Set(event.data.entryIds)
    next = setSectionList(
      next,
      'treatment',
      getSectionList(next, 'treatment').filter(
        (item) => !ids.has(item.entryId) || item.detailsCompletedAt || item.medicineLogId,
      ),
    )
  }
  if (event.section && event.entryId) {
    const list = getSectionList(next, event.section)
    const entry = list.find((item) => item.entryId === event.entryId)
    if (entry) {
      const keep = !!(entry.detailsCompletedAt || entry.medicineLogId)
      next = setSectionList(
        next,
        event.section,
        keep
          ? list.map((item) =>
              item.entryId === event.entryId ? { ...item, liveEventId: undefined } : item,
            )
          : list.filter((item) => item.entryId !== event.entryId),
      )
    }
  }
  return next
}

// Change the time on an event (and its entry) - crews correct a tap made late.
export const retimeEvent = (record, logKey, eventId, time) => {
  const log = record?.[logKey]
  if (!log?.events) return record
  const event = log.events.find((item) => item.id === eventId)
  if (!event) return record
  let next = {
    ...record,
    [logKey]: {
      ...log,
      events: log.events.map((item) =>
        item.id === eventId || item.data?.cause === eventId ? { ...item, time } : item,
      ),
    },
  }
  if (event.data?.entryIds?.length) {
    const ids = new Set(event.data.entryIds)
    next = setSectionList(
      next,
      'treatment',
      getSectionList(next, 'treatment').map((item) =>
        ids.has(item.entryId) && !item.medicineLogId ? { ...item, dateTime: time } : item,
      ),
    )
  }
  if (event.section && event.entryId) {
    const key = entryTimeKey(event.section)
    next = setSectionList(
      next,
      event.section,
      // A stock-linked administration's time lives in its MedicineLog too -
      // change that through the amend flow, not by retiming the tap
      getSectionList(next, event.section).map((item) =>
        item.entryId === event.entryId && !item.medicineLogId ? { ...item, [key]: time } : item,
      ),
    )
  }
  return next
}

// ---------------------------------------------------------------------------
// Entries and details
// ---------------------------------------------------------------------------

export const findEntry = (record, section, entryId) =>
  getSectionList(record, section).find((item) => item.entryId === entryId) || null

const findEventAnywhere = (record, eventId) => {
  for (const logKey of Object.values(LOG_KEYS)) {
    const event = record?.[logKey]?.events?.find((item) => item.id === eventId)
    if (event) return { logKey, event }
  }
  return null
}

// The detail form for an entry created from a tap (null for ordinary entries).
export const getEntryForm = (entry) => {
  if (!entry?.liveAction) return null
  const action = getAction(entry.liveAction)
  return action?.detailForm ? getForm(action.detailForm) : null
}

// Merge detail-form values into an entry and clear its pending flag. Empty
// values remove the key so the generic entry displays stay tidy. For deferred
// (vitals) targets with no entry yet, the entry is created here.
export const completeEntryDetails = (record, section, entryId, values, { eventId } = {}) => {
  const list = getSectionList(record, section)
  const exists = list.some((item) => item.entryId === entryId)
  const clean = {}
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return
    if (Array.isArray(value) && !value.length) return
    clean[key] = value
  })
  const stamp = { detailsPending: false, detailsCompletedAt: nowIso() }

  if (exists) {
    return setSectionList(
      record,
      section,
      list.map((item) => {
        if (item.entryId !== entryId) return item
        const merged = { ...item }
        Object.keys(values || {}).forEach((key) => {
          if (!(key in clean)) delete merged[key]
        })
        return { ...merged, ...clean, ...stamp }
      }),
    )
  }

  // Deferred entry (e.g. a GCS/obs tap whose obs set is only written once
  // the numbers are in)
  const found = eventId ? findEventAnywhere(record, eventId) : null
  if (!found) return record
  const action = getAction(found.event.type)
  const timeKey = entryTimeKey(section)
  const entry = {
    ...(section === 'vitals' ? {} : { moduleName: action?.target?.moduleName }),
    [timeKey]: found.event.time,
    entryId,
    liveEventId: eventId,
    liveAction: found.event.type,
    source: 'live',
    ...clean,
    ...stamp,
  }
  let next = setSectionList(record, section, [...list, entry])
  const log = next[found.logKey]
  next = {
    ...next,
    [found.logKey]: {
      ...log,
      events: log.events.map((item) =>
        item.id === eventId ? { ...item, section, entryId } : item,
      ),
    },
  }
  return next
}

// Mark an entry's details as not needed (crew dismissed the reminder).
export const dismissEntryDetails = (record, section, entryId) =>
  setSectionList(
    record,
    section,
    getSectionList(record, section).map((item) =>
      item.entryId === entryId ? { ...item, detailsPending: false } : item,
    ),
  )

// Replace a stub entry with another entry (a medicine stub linked to tracked
// stock becomes the 'Medicine Administration' entry the bag flow returns).
// The stub's entryId/liveEventId carry over so the timeline link survives.
// If the stub vanished while the stock call was in flight (undone/deleted),
// the replacement is appended instead - the stock is already used, so the
// administration must not be left without an ePCR entry.
export const replaceEntry = (record, section, entryId, replacement) => {
  const list = getSectionList(record, section)
  const stub = list.find((item) => item.entryId === entryId)
  const linked = {
    ...replacement,
    entryId,
    liveEventId: stub?.liveEventId,
    liveAction: stub?.liveAction,
    source: 'live',
    detailsPending: false,
    detailsCompletedAt: nowIso(),
  }
  if (!stub) return setSectionList(record, section, [...list, linked])
  return setSectionList(
    record,
    section,
    list.map((item) => (item.entryId === entryId ? linked : item)),
  )
}

// Everything still waiting for details, oldest first:
// [{ section, entry, event, action, form }]
export const listPendingDetails = (record) => {
  const events = {}
  Object.values(LOG_KEYS).forEach((logKey) => {
    ;(record?.[logKey]?.events || []).forEach((event) => {
      if (!event.voided) events[event.id] = event
    })
  })
  const pending = []
  Object.keys(SECTION_PATHS).forEach((section) => {
    getSectionList(record, section).forEach((entry) => {
      if (!entry?.detailsPending) return
      const action = getAction(entry.liveAction)
      pending.push({
        section,
        entry,
        event: events[entry.liveEventId] || null,
        action,
        form: action?.detailForm ? getForm(action.detailForm) : null,
      })
    })
  })
  // Deferred targets (vitals) with no entry yet
  Object.values(events).forEach((event) => {
    const action = getAction(event.type)
    if (!action?.target?.deferEntry || event.entryId || event.data?.skipped) return
    pending.push({
      section: action.target.section,
      entry: null,
      event,
      action,
      form: action.detailForm ? getForm(action.detailForm) : null,
    })
  })
  const timeOf = (item) =>
    new Date(item.event?.time || item.entry?.dateTime || item.entry?.time || 0).getTime()
  return pending.sort((a, b) => timeOf(a) - timeOf(b))
}

// Skip a deferred (no-entry) detail prompt for good.
export const skipDeferredDetails = (record, logKey, eventId) => {
  const log = record?.[logKey]
  if (!log?.events) return record
  return {
    ...record,
    [logKey]: {
      ...log,
      events: log.events.map((item) =>
        item.id === eventId ? { ...item, data: { ...(item.data || {}), skipped: true } } : item,
      ),
    },
  }
}

// ---------------------------------------------------------------------------
// Deriving console state from the log
// ---------------------------------------------------------------------------

const live = (events) => (events || []).filter((event) => !event.voided)

export const countEvents = (events, type, predicate = () => true) =>
  live(events).filter((event) => event.type === type && predicate(event)).length

export const lastEvent = (events, types) => {
  const wanted = Array.isArray(types) ? types : [types]
  const list = live(events)
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (wanted.includes(list[i].type)) return list[i]
  }
  return null
}

// { [toggleKey]: { on, since } } for every toggle action seen in the log.
export const getToggleStates = (events) => {
  const states = {}
  live(events).forEach((event) => {
    const action = getAction(event.type)
    if (action?.kind !== 'toggle') return
    states[event.type] = { on: !!event.data?.on, since: event.time }
  })
  return states
}

// Total seconds a toggle has been on (CPR → compression time; REBOA →
// occlusion time), up to `until` (default now).
export const getToggleOnSeconds = (events, type, until = Date.now()) => {
  let total = 0
  let onSince = null
  live(events)
    .filter((event) => event.type === type)
    .forEach((event) => {
      const t = new Date(event.time).getTime()
      if (event.data?.on) {
        if (onSince === null) onSince = t
      } else if (onSince !== null) {
        total += t - onSince
        onSince = null
      }
    })
  if (onSince !== null) total += until - onSince
  return Math.max(0, Math.floor(total / 1000))
}

// Arrest phase: 'arrest' | 'rosc' | 'ended'. ROSC moves to the post-ROSC
// screen, re-arrest returns to 'arrest', ROLE/terminated ends.
// Older mobile builds still write 'ROSC' / 'Life Extinct' events.
const LEGACY_PHASES = { ROSC: 'rosc', 'Life Extinct': 'ended' }

export const getArrestPhase = (events) => {
  const last = lastEvent(events, [
    ...Object.keys(ACTIONS).filter((key) => ACTIONS[key].phaseTo),
    ...Object.keys(LEGACY_PHASES),
  ])
  if (!last) return 'arrest'
  return getAction(last.type)?.phaseTo || LEGACY_PHASES[last.type] || 'arrest'
}

// Cycle timer: restarts on the last rhythm check / shock (or arrest start).
export const getCycleState = (log, cycleLengthSeconds, now = Date.now()) => {
  if (!log?.startedAt) return { remainingSeconds: cycleLengthSeconds, overdue: false }
  const restarters = Object.keys(ACTIONS).filter((key) => ACTIONS[key].restartsCycle)
  const last = lastEvent(log.events, [...restarters, 'Shock', 'No Shock', 'reArrest'])
  const since = new Date(last ? last.time : log.startedAt).getTime()
  const remainingSeconds = cycleLengthSeconds - Math.floor((now - since) / 1000)
  return { remainingSeconds, overdue: remainingSeconds < 0 }
}

// Live clocks for a console: [{ key, label, seconds, tone, warn }]
// Clock definitions live on actions (`clock`):
//   since      - time since the last tap of this action (adrenaline)
//   whileOn    - running while a toggle is on (tourniquet, REBOA, cooling)
//   sinceFirst - time since the first tap (time of birth, seizure onset)
export const getClocks = (events, actionKeys, now = Date.now()) => {
  const clocks = []
  const toggles = getToggleStates(events)
  actionKeys.forEach((key) => {
    const action = getAction(key)
    const clock = action?.clock
    if (!clock) return
    let from = null
    if (clock.mode === 'whileOn') {
      if (toggles[key]?.on) from = toggles[key].since
    } else if (clock.mode === 'sinceFirst') {
      const first = live(events).find((event) => event.type === key)
      from = first?.time || null
    } else {
      from = lastEvent(events, key)?.time || null
    }
    if (!from) return
    const seconds = Math.max(0, Math.floor((now - new Date(from).getTime()) / 1000))
    clocks.push({
      key,
      label: clock.label || action.label,
      seconds,
      tone: action.tone,
      warn: !!(clock.warnAfterSeconds && seconds >= clock.warnAfterSeconds),
      count: clock.showCount ? countEvents(events, key) : undefined,
    })
  })
  return clocks
}

// Chest compression fraction (%) between arrest start and now/ROSC, from
// CPR on/off taps. Only meaningful when the crew logged pauses.
export const getCompressionFraction = (log, now = Date.now()) => {
  if (!log?.startedAt) return null
  const cprEvents = live(log.events).filter((event) => event.type === 'cpr')
  if (cprEvents.length < 2) return null
  const end =
    getArrestPhase(log.events) === 'arrest'
      ? now
      : new Date(lastEvent(log.events, ['rosc', 'role', 'terminated'])?.time || now).getTime()
  const total = end - new Date(log.startedAt).getTime()
  if (total <= 0) return null
  const on = getToggleOnSeconds(log.events, 'cpr', end) * 1000
  return Math.min(100, Math.round((on / total) * 100))
}

// ---------------------------------------------------------------------------
// Consoles
// ---------------------------------------------------------------------------

// The action groups to show for a console + phase, filtered by level.
// Returns [{ title, actions: [action] }]
export const getConsoleGroups = (consoleKey, { phase, level } = {}) => {
  const consoleDef = CONSOLES[consoleKey]
  if (!consoleDef) return []
  const groups = consoleDef.phases ? consoleDef.phases[phase || 'arrest']?.groups : consoleDef.groups
  return (groups || [])
    .map((group) => ({
      ...group,
      actions: group.actions.map(getAction).filter((action) => isActionAvailable(action, level)),
    }))
    .filter((group) => group.actions.length)
}

export const getConsoleActionKeys = (consoleKey, opts) =>
  getConsoleGroups(consoleKey, opts).flatMap((group) => group.actions.map((action) => action.key))

// Merged, time-ordered timeline across both logs for review screens/PDF.
export const getMergedTimeline = (record, { includeVoided = false } = {}) => {
  const items = []
  Object.entries(LOG_KEYS).forEach(([source, logKey]) => {
    ;(record?.[logKey]?.events || []).forEach((event) => {
      if (event.voided && !includeVoided) return
      items.push({ ...event, source })
    })
  })
  return items.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
}

// Quick drugs for a console category: tenant list (criticalCareSettings.
// quickDrugs) when set, else the catalogue defaults. Arrest console also
// merges the org's cardiac arrest protocol drugs.
export const getQuickDrugs = ({ category, tenantDrugs, defaults, arrestProtocolDrugs }) => {
  const source = tenantDrugs?.length ? tenantDrugs : defaults || []
  const filtered = source.filter(
    (drug) => !drug.categories?.length || !category || drug.categories.includes(category),
  )
  if (!arrestProtocolDrugs?.length) return filtered
  const seen = new Set(filtered.map((drug) => `${drug.name}|${drug.dose}`))
  return [
    ...arrestProtocolDrugs.filter((drug) => !seen.has(`${drug.name}|${drug.dose}`)),
    ...filtered,
  ]
}

// Display helper
export const formatClock = (totalSeconds) => {
  const sign = totalSeconds < 0 ? '-' : ''
  const abs = Math.abs(totalSeconds)
  const hours = Math.floor(abs / 3600)
  const minutes = String(Math.floor((abs % 3600) / 60)).padStart(2, '0')
  const seconds = String(abs % 60).padStart(2, '0')
  return hours > 0 ? `${sign}${hours}:${minutes}:${seconds}` : `${sign}${minutes}:${seconds}`
}

// showIf: { key, in: [...] } | { key, notIn: [...] } | { key, truthy: true }
// | { all: [conditions] } | { any: [conditions] }
export const fieldVisible = (field, values) => {
  const rule = field.showIf
  if (!rule) return true
  const test = (condition) => {
    if (condition.all) return condition.all.every(test)
    if (condition.any) return condition.any.some(test)
    const value = values?.[condition.key]
    const list = Array.isArray(value) ? value : [value]
    if (condition.in) return list.some((item) => condition.in.includes(item))
    if (condition.notIn) return !list.some((item) => condition.notIn.includes(item))
    if (condition.truthy) return Array.isArray(value) ? value.length > 0 : !!value
    return true
  }
  return test(rule)
}

// Values a detail form should start with: the entry's own values for the
// form's fields (so re-opening edits, not overwrites) plus anything the
// form computes from the log (attempt number, times).
export const getFormInitialValues = (form, entry) => {
  const values = {}
  ;(form?.fields || []).forEach((field) => {
    if (entry && entry[field.key] !== undefined) values[field.key] = entry[field.key]
    else if (field.default !== undefined) values[field.key] = field.default
  })
  return values
}

// Fields required by a detail form that are still empty.
export const missingRequiredFields = (form, values) =>
  (form?.fields || []).filter(
    (field) =>
      field.required &&
      fieldVisible(field, values) &&
      (values?.[field.key] === undefined ||
        values?.[field.key] === '' ||
        (Array.isArray(values?.[field.key]) && !values[field.key].length)),
  )

// Cognitive-aid prompts for the arrest console - reminders to consider, never
// instructions. [{ key, tone, text }]
//   - refractory VF: 3+ shocks (critical care level, where DSED/VC exist)
//   - maternal arrest: obstetric category, 4 minutes into the arrest
//   - traumatic arrest: HOTT actions used and 20 minutes elapsed
export const getArrestPrompts = (record, { level, now = Date.now() } = {}) => {
  const log = record?.cardiacArrest
  if (!log?.startedAt) return []
  const events = log.events || []
  if (getArrestPhase(events) !== 'arrest') return []
  const prompts = []
  const elapsed = Math.floor((now - new Date(log.startedAt).getTime()) / 1000)
  const shocks = live(events).filter((event) =>
    ['shock', 'dsed', 'vectorChange', 'Shock'].includes(event.type),
  ).length
  if (level === 'Critical Care' && shocks >= 3) {
    prompts.push({
      key: 'refractoryVf',
      tone: 'amber',
      text: `${shocks} shocks - refractory VF? Consider DSED or vector change per your SOP.`,
    })
  }
  const categories = record?.criticalCare?.categories || []
  if (categories.includes('obstetric') && elapsed >= 240) {
    prompts.push({
      key: 'maternalArrest',
      tone: 'red',
      text: 'Maternal arrest over 4 minutes - uterine displacement / resuscitative hysterotomy decision point.',
    })
  }
  const hottKeys = ['thoracostomy', 'needleDecompression', 'thoracotomy', 'bloodProduct', 'reboa', 'pelvicBinder']
  const traumatic =
    categories.some((key) => ['majorTrauma', 'codeRed'].includes(key)) ||
    live(events).some((event) => hottKeys.includes(event.type))
  if (traumatic && elapsed >= 1200) {
    prompts.push({
      key: 'tcaDecision',
      tone: 'graphite',
      text: 'Traumatic arrest at 20 minutes - review HOTT interventions and ongoing resuscitation.',
    })
  }
  return prompts
}
